// Alerts (doc 12 §3): rules on signals, the events they open and close, and the phones to notify.
import { z } from 'zod';
import { objectIdSchema } from './common';

export const ALERT_SEVERITIES = ['high', 'medium', 'low'] as const;
export const alertSeveritySchema = z.enum(ALERT_SEVERITIES);
export type AlertSeverity = z.infer<typeof alertSeveritySchema>;

/** `no_data`: the signal has had no value for `holdSec`. */
export const ALERT_CONDITIONS = ['above', 'below', 'no_data'] as const;
export const alertConditionSchema = z.enum(ALERT_CONDITIONS);
export type AlertCondition = z.infer<typeof alertConditionSchema>;

export const ALERT_NO_DATA_MIN_SEC = 60;

const ruleFields = {
  name: z.string().trim().min(1).max(120),
  signalId: objectIdSchema,
  condition: alertConditionSchema,
  /** The limit, in `unitId`. Not used by `no_data`. */
  threshold: z.number().finite().nullable().default(null),
  /** Unit of `threshold` and `deadband`: any unit of the signal's quantity. Default: its storage unit. */
  unitId: z.string().max(64).nullable().default(null),
  /** above / below: seconds the value must stay beyond the limit. no_data: seconds without a value. */
  holdSec: z.number().int().min(0).max(86_400).default(0),
  /** The alert clears once the value is back inside the limit by this much. */
  deadband: z.number().finite().min(0).default(0),
  severity: alertSeveritySchema.default('medium'),
  enabled: z.boolean().default(true),
  /** Contact groups told when the alert opens and clears (Channels tab). */
  contactGroupIds: z.array(objectIdSchema).max(10).default([]),
  /** "Questionable": when the alert opens, the supervisors of the well are asked what happened (doc 14). */
  askSupervisors: z.boolean().default(false),
  /** Minutes before a question without an answer is sent again. */
  reminderMin: z.number().int().min(5).max(240).default(15),
  /** How many times it is sent again before it is marked unanswered. */
  maxReminders: z.number().int().min(0).max(5).default(2),
};

export const alertRuleInputSchema = z
  .object(ruleFields)
  .refine((r) => r.condition === 'no_data' || r.threshold !== null, { message: 'A limit is required', path: ['threshold'] })
  .refine((r) => r.condition !== 'no_data' || r.holdSec >= ALERT_NO_DATA_MIN_SEC, { message: `At least ${ALERT_NO_DATA_MIN_SEC} seconds`, path: ['holdSec'] });
export type AlertRuleInput = z.input<typeof alertRuleInputSchema>;

/** A change to a rule. The signal of a rule does not change: make another rule. */
export const alertRulePatchSchema = z.object(ruleFields).omit({ signalId: true }).partial();
export type AlertRulePatch = z.input<typeof alertRulePatchSchema>;

export const alertRuleSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  signalId: objectIdSchema,
  signalName: z.string(),
  fieldId: objectIdSchema,
  wellId: objectIdSchema,
  condition: alertConditionSchema,
  /** In `unitId`, the unit the caller reads this signal in. */
  threshold: z.number().nullable(),
  deadband: z.number(),
  unitId: z.string(),
  holdSec: z.number(),
  severity: alertSeveritySchema,
  enabled: z.boolean(),
  /** An alert of this rule is open right now. */
  active: z.boolean(),
  contactGroupIds: z.array(objectIdSchema),
  askSupervisors: z.boolean(),
  reminderMin: z.number(),
  maxReminders: z.number(),
  updatedAt: z.string(),
});
export type AlertRule = z.infer<typeof alertRuleSchema>;

export const alertEventSchema = z.object({
  id: objectIdSchema,
  ruleId: objectIdSchema,
  name: z.string(),
  signalId: objectIdSchema,
  signalName: z.string(),
  fieldId: objectIdSchema,
  wellId: objectIdSchema,
  condition: alertConditionSchema,
  severity: alertSeveritySchema,
  state: z.enum(['active', 'cleared']),
  startedAt: z.string(),
  clearedAt: z.string().nullable(),
  /** Values in `unitId`, the unit the caller reads this signal in; null for `no_data`. */
  threshold: z.number().nullable(),
  /** The value that opened the alert. */
  value: z.number().nullable(),
  /** The furthest the value went beyond the limit. */
  peak: z.number().nullable(),
  /** The latest value while the alert is open. */
  lastValue: z.number().nullable(),
  unitId: z.string(),
  decimals: z.number().nullable(),
});
export type AlertEvent = z.infer<typeof alertEventSchema>;

export const alertListQuerySchema = z.object({
  /** `active`: open now. `cleared`: closed within `days`. `all`: both. */
  state: z.enum(['active', 'cleared', 'all']).default('active'),
  days: z.coerce.number().int().min(1).max(90).default(7),
  fieldId: objectIdSchema.optional(),
  wellId: objectIdSchema.optional(),
});

export const alertListResponseSchema = z.object({
  items: z.array(alertEventSchema),
  /** Open alerts the caller can see, whatever the filter. */
  active: z.object({ total: z.number(), high: z.number(), medium: z.number(), low: z.number() }),
});
export type AlertListResponse = z.infer<typeof alertListResponseSchema>;

// ---- Phones to notify ----
/** An Expo push token: `ExponentPushToken[…]` or `ExpoPushToken[…]`. */
export const pushTokenSchema = z.string().regex(/^Expo(nent)?PushToken\[[A-Za-z0-9_-]{10,100}\]$/, 'Not a push token');
export const pushDeviceRequestSchema = z.object({
  token: pushTokenSchema,
  platform: z.enum(['ios', 'android']),
  /** Language of the notifications. */
  language: z.enum(['en', 'fr', 'ar']).default('en'),
});
export type PushDeviceRequest = z.input<typeof pushDeviceRequestSchema>;
export const pushDeviceRemoveSchema = z.object({ token: pushTokenSchema });

// ---- Notification channels (doc 12 §3.1) ----
export const NOTIFY_CHANNELS = ['email', 'whatsapp', 'teams', 'slack', 'webhook'] as const;
export type NotifyChannel = (typeof NOTIFY_CHANNELS)[number];
export const HOOK_KINDS = ['teams', 'slack', 'webhook'] as const;
export type HookKind = (typeof HOOK_KINDS)[number];

/** A phone number in international form: +, country code, number (E.164). */
export const phoneNumberSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s().-]/g, ''))
  .pipe(z.string().regex(/^\+[1-9]\d{6,14}$/, 'Use the international form, e.g. +216 20 123 456'));

/**
 * A Teams, Slack or other web link. The address is written only: it holds the channel's secret,
 * so it is stored encrypted and read back as its host. On a change, leaving `url` out keeps it.
 */
export const hookInputSchema = z.object({
  id: objectIdSchema.optional(),
  kind: z.enum(HOOK_KINDS),
  name: z.string().trim().min(1).max(80),
  url: z.string().trim().url().max(2000).optional(),
  /** Generic webhook only: signs each request (X-TwinLink-Signature: sha256=HMAC of the body). Empty string removes it. */
  secret: z.string().max(200).optional(),
});
export type HookInput = z.infer<typeof hookInputSchema>;

const groupFields = {
  name: z.string().trim().min(1).max(80),
  enabled: z.boolean().default(true),
  /** The lowest severity the group is told about. */
  minSeverity: alertSeveritySchema.default('low'),
  /** Also a message when the alert is back to normal. */
  notifyOnClear: z.boolean().default(true),
  language: z.enum(['en', 'fr', 'ar']).default('en'),
  emails: z.array(z.string().trim().toLowerCase().email().max(254)).max(50).default([]),
  whatsapp: z.array(phoneNumberSchema).max(30).default([]),
  hooks: z.array(hookInputSchema).max(10).default([]),
};
export const contactGroupInputSchema = z.object(groupFields);
export type ContactGroupInput = z.input<typeof contactGroupInputSchema>;
export const contactGroupPatchSchema = z.object(groupFields).partial();
export type ContactGroupPatch = z.input<typeof contactGroupPatchSchema>;

export const contactGroupSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  enabled: z.boolean(),
  minSeverity: alertSeveritySchema,
  notifyOnClear: z.boolean(),
  language: z.enum(['en', 'fr', 'ar']),
  emails: z.array(z.string()),
  whatsapp: z.array(z.string()),
  hooks: z.array(z.object({ id: objectIdSchema, kind: z.enum(HOOK_KINDS), name: z.string(), host: z.string(), hasSecret: z.boolean() })),
  /** Rules that notify this group. */
  ruleCount: z.number(),
  updatedAt: z.string(),
});
export type ContactGroup = z.infer<typeof contactGroupSchema>;

export const contactGroupListSchema = z.object({
  items: z.array(contactGroupSchema),
  /** Channels this server can send on: WhatsApp needs SmarDac Messenger in its settings. */
  channels: z.object({ email: z.boolean(), whatsapp: z.boolean(), teams: z.boolean(), slack: z.boolean(), webhook: z.boolean() }),
});
export type ContactGroupList = z.infer<typeof contactGroupListSchema>;

export const notificationLogSchema = z.object({
  id: objectIdSchema,
  at: z.string(),
  groupName: z.string(),
  channel: z.enum(NOTIFY_CHANNELS),
  /** Email address, phone number, or the name of the link. */
  to: z.string(),
  /** What the message was about. */
  kind: z.enum(['opened', 'cleared', 'test']),
  alertName: z.string().nullable(),
  status: z.enum(['queued', 'sent', 'failed', 'skipped']),
  error: z.string().nullable(),
});
export type NotificationLog = z.infer<typeof notificationLogSchema>;
export const notificationLogQuerySchema = z.object({ limit: z.coerce.number().int().min(1).max(500).default(100) });
